import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { PlusCircle, Trash2, Package, Star } from "lucide-react";

export default function Products({ products, refetchAll }) {
  const { isOwner } = useAuth();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Enter a product name");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/products`, { name: name.trim() });
      toast.success(`${name.trim()} added to catalog`);
      setName("");
      await refetchAll();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That product already exists");
      } else {
        toast.error("Failed to add product");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, productName) => {
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success(`${productName} removed`);
      await refetchAll();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const toggleFav = async (p) => {
    try {
      await axios.patch(`${API}/products/${p.id}`, { favorite: !p.favorite });
      await refetchAll();
    } catch {
      toast.error("Failed to update");
    }
  };

  const renameProduct = async (id, name) => {
    try {
      await axios.patch(`${API}/products/${id}`, { name });
      toast.success("Product renamed");
      await refetchAll();
    } catch {
      toast.error("Rename failed");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-4xl w-full mx-auto" data-testid="products-page">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">
          Product catalog
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Manage your products
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Star a product to make it appear in the Lead Entry dropdown. Un-starred products stay in your catalog but stay out of the dropdown.
        </p>
      </header>

      <form
        onSubmit={add}
        data-testid="product-add-form"
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-wrap items-end gap-3 mb-6"
      >
        <div className="flex-1 min-w-[240px]">
          <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block">
            Product name *
          </label>
          <input
            data-testid="product-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. TRAVEL SLING PRO"
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>
        <button
          data-testid="add-product-btn"
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-md"
          style={{ transition: "background-color 160ms ease" }}
        >
          <PlusCircle size={16} />
          Add product
        </button>
      </form>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Package size={16} className="text-slate-500" />
          <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
            Catalog ({products.length})
          </h2>
        </div>
        <ul className="divide-y divide-slate-100">
          {products.length === 0 && (
            <li className="py-12 text-center text-sm text-slate-500">
              No products yet. Add your first product above.
            </li>
          )}
          {products.map((p, idx) => (
            <li
              key={p.id}
              data-testid={`product-row-${idx}`}
              className="px-5 py-4 flex items-center justify-between row-hover"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                  <Package size={16} />
                </div>
                <input
                  data-testid={`edit-product-name-${idx}`}
                  defaultValue={p.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== p.name) renameProduct(p.id, v);
                  }}
                  className="font-semibold text-slate-900 text-sm bg-transparent border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none px-0.5"
                />
                {p.favorite && (
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 font-semibold">
                    In dropdown
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={`fav-product-${idx}`}
                  onClick={() => toggleFav(p)}
                  className={`p-2 ${p.favorite ? "text-amber-500 hover:text-amber-600" : "text-slate-300 hover:text-amber-400"}`}
                  style={{ transition: "color 160ms ease" }}
                  aria-label="Toggle favorite"
                  title={p.favorite ? "Shown in Lead Entry — click to hide" : "Star to show in Lead Entry"}
                >
                  <Star size={17} fill={p.favorite ? "currentColor" : "none"} />
                </button>
                {isOwner && (
                  <button
                    data-testid={`delete-product-${idx}`}
                    onClick={() => remove(p.id, p.name)}
                    className="text-slate-400 hover:text-rose-600 p-2"
                    style={{ transition: "color 160ms ease" }}
                    aria-label="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
